import React, { useContext, createContext } from 'react';
import styled, { css, ThemeContext } from 'styled-components';
import { rem } from 'polished';
import { TextField, IconButton, TextFieldProps, InputAdornment } from '@material-ui/core';
import { Search, Close } from '@material-ui/icons';
import { Icon } from 'components';

type SearchBoxVariant = 'standard' | 'outlined';
export type SearchBoxChrome = 'default' | 'sci' | 'dark' | 'light';

export function isSciSearchChrome(chrome: SearchBoxChrome | undefined) {
  return chrome === 'sci' || chrome === 'dark' || chrome === 'light';
}

export interface SearchBoxProps {
  variant?: SearchBoxVariant;
  /** SCI-style search chrome for Summary (and similar). */
  chrome?: SearchBoxChrome;
  value: string;
  placeholder: string;
  onClear(): void;
  onSearch?(val: string | undefined): void;
  /** Si es false, Enter no dispara onSearch (solo lupa o handler externo). Default true. */
  submitOnEnter?: boolean;
}

const StartSearchAdornment = styled(InputAdornment)`
  && {
    height: 100%;
    max-height: none;
    margin: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  &.search-box-sci-start-icon {
    color: #94a3b8;
  }

  body[data-theme='dark'] &.search-box-sci-start-icon {
    color: #448aff;
  }
`;

const StyledTextField = styled(({ inputProps, InputProps: InputPropsFromParent, $chrome, ...props }) => (
  <TextField
    {...props}
    InputProps={{
      classes: { input: 'input', root: 'root', adornedStart: 'adornedStart', adornedEnd: 'adornedEnd' },
      ...InputPropsFromParent,
      ...inputProps
    }}
  />
))<{ $chrome: SearchBoxChrome }>`
  & .input {
    font-size: ${rem('15px')};
    color: ${props => props.theme.colors.S['800']};
    padding: ${rem('8px')} ${rem('8px')};

    ::placeholder {
      color: ${props => {
        if (isSciSearchChrome(props.$chrome)) return '#94A3B8';
        return props.theme.colors.S['600'];
      }};
      font-size: ${props =>
        isSciSearchChrome(props.$chrome) ? rem('14px') : 'inherit'};
      opacity: 1;
    }
  }

  & .MuiOutlinedInput-root {
    height: ${rem('40px')};
    background-color: #fff;
    border-radius: ${props => (props.$chrome === 'default' ? rem('4px') : rem('7px'))};

    & .MuiOutlinedInput-input {
      padding: 0 ${rem('10px')};
    }

    .MuiOutlinedInput-notchedOutline {
      border-color: ${props =>
        isSciSearchChrome(props.$chrome) ? '#E2E8F0' : props.theme.colors.S['400']};
    }

    &.Mui-error .MuiOutlinedInput-notchedOutline {
      border-color: #d32f2f !important;
    }

    &.Mui-error:hover .MuiOutlinedInput-notchedOutline {
      border-color: #b71c1c !important;
    }

    &:hover:not(.Mui-disabled):not(.Mui-focused):not(.Mui-error) .MuiOutlinedInput-notchedOutline {
      border-color: ${props =>
        isSciSearchChrome(props.$chrome) ? '#E2E8F0' : props.theme.colors.S['400']};
    }
  }

  ${props =>
    isSciSearchChrome(props.$chrome) &&
    css`
      body[data-theme='dark'] & {
        & .input,
        & .MuiOutlinedInput-input {
          color: #ffffff;
        }

        & .input::placeholder,
        & .MuiOutlinedInput-input::placeholder {
          color: #8899a6;
        }

        & .MuiOutlinedInput-root {
          background-color: #263e59;
        }

        & .MuiOutlinedInput-notchedOutline,
        & .MuiOutlinedInput-root:hover:not(.Mui-disabled):not(.Mui-focused):not(.Mui-error)
          .MuiOutlinedInput-notchedOutline {
          border-color: rgba(30, 80, 255, 0.14);
        }

        & .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline {
          border-color: rgba(68, 138, 255, 0.45);
        }

        & .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) {
          box-shadow: 0 0 0 3px rgba(68, 138, 255, 0.15);
        }

        & .MuiInputAdornment-positionEnd .MuiSvgIcon-root {
          color: rgba(255, 255, 255, 0.7);
        }
      }
    `}

  & .MuiFormHelperText-root {
    margin-left: 0;
    margin-right: 0;
  }

  & .MuiOutlinedInput-adornedEnd {
    padding-right: 0;
  }

  & .MuiOutlinedInput-adornedStart {
    padding-left: ${props =>
      props.$chrome === 'default' ? 0 : rem('12px')};
  }

  & .MuiOutlinedInput-root {
    .MuiInputAdornment-root {
      height: 100%;
      max-height: none;
      margin: 0;
      display: flex;
      align-items: center;
    }

    .MuiInputAdornment-positionStart {
      margin-right: 0;
    }
  }

  ${props =>
    isSciSearchChrome(props.$chrome) &&
    css`
      & .MuiOutlinedInput-input {
        padding-left: ${rem('6px')};
        padding-right: ${rem('10px')};
      }

      & .MuiInputAdornment-positionStart .MuiSvgIcon-root,
      & .MuiInputAdornment-positionStart svg {
        display: block;
        font-size: ${rem('18px')};
      }
    `}

  & .MuiOutlinedInput-root.Mui-focused:not(.Mui-error) .MuiOutlinedInput-notchedOutline {
    border-color: ${props => props.theme.colors.Mi['400']};
  }

  & .MuiOutlinedInput-root.Mui-error.Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: #d32f2f !important;
  }

  & .MuiInput-underline {
    &::before {
      border-bottom: ${rem('1px')} solid ${props => props.theme.colors.S['600']};
    }

    &:hover::before {
      border-bottom-color: ${props => props.theme.colors.S['600']};
    }

    &::after {
      border-bottom-color: ${props => props.theme.colors.S['800']};
    }
  }
`;

const StyledIconButton = styled(IconButton)<{ transparent?: boolean }>`
  ${props =>
    props.transparent &&
    css`
      &:hover {
        background-color: transparent;
      }
    `}
  border-radius: initial;
  border-top-right-radius: ${rem('4px')};
  border-bottom-right-radius: ${rem('4px')};
  padding: ${rem('9px')};

  &:hover {
    background-color: ${props => props.theme.colors.Mi[100]};
  }

  &:active {
    background-color: ${props => props.theme.colors.Mi[200]};
  }

  ${props =>
    props.transparent &&
    css`
      &:hover {
        background-color: transparent;
      }

      &:active {
        background-color: transparent;
      }
    `}
`;

type SearchContextProps = {
  variant: SearchBoxVariant;
  chrome: SearchBoxChrome;
  value: string;
  onSearch(val: string | undefined): void;
  onClear(): void;
};

const SearchBoxContext = createContext<Partial<SearchContextProps>>({
  value: '',
  chrome: 'default'
});

function SearchIconButton() {
  const theme = useContext(ThemeContext);
  const { onSearch, value, chrome } = useContext(SearchBoxContext);
  const onClickSearch = (val: string | undefined) => (_e: any) => {
    if (onSearch) {
      onSearch(val);
    }
  };

  const iconColor = isSciSearchChrome(chrome) ? '#448aff' : theme.colors.Mi[400];

  if (chrome === 'light') {
    return (
      <StyledIconButton
        disableRipple
        disabled={value?.length === 0}
        onClick={onClickSearch(value)}
        style={{
          backgroundColor: '#A6A6A6',
          borderRadius: '0 7px 7px 0',
          width: 40,
          height: 40
        }}
      >
        <Icon Component={Search} color={iconColor} />
      </StyledIconButton>
    );
  }

  return (
    <StyledIconButton disableRipple disabled={value?.length === 0} onClick={onClickSearch(value)}>
      <Icon Component={Search} color={iconColor} />
    </StyledIconButton>
  );
}

function StartAdornment() {
  const theme = useContext(ThemeContext);
  const { variant, chrome } = useContext(SearchBoxContext);

  if (isSciSearchChrome(chrome)) {
    return (
      <StartSearchAdornment position="start" className="search-box-sci-start-icon">
        <Icon Component={Search} color="inherit" fontSize={rem('18px')} />
      </StartSearchAdornment>
    );
  }

  if (variant === undefined || variant === 'standard') {
    return (
      <InputAdornment position="start">
        <Icon Component={Search} color={theme.colors.S[600]} />
      </InputAdornment>
    );
  }
  return null;
}

function EndAdornment() {
  const { variant, onClear, value, chrome } = useContext(SearchBoxContext);
  const clearColor = undefined;

  if (value) {
    return (
      <>
        {value.length > 0 && (
          <InputAdornment position="end">
            <StyledIconButton transparent disableRipple aria-label="clear" onClick={onClear}>
              <Icon Component={Close} color={clearColor} />
            </StyledIconButton>
          </InputAdornment>
        )}
        {variant === 'outlined' && !isSciSearchChrome(chrome) ? <SearchIconButton /> : null}
      </>
    );
  }

  return <>{variant === 'outlined' && !isSciSearchChrome(chrome) ? <SearchIconButton /> : null}</>;
}

export const SearchBox = React.memo(function SearchBox({
  value,
  onClear,
  variant,
  chrome = 'default',
  onSearch,
  inputProps: inputPropsFromParent,
  submitOnEnter = true,
  InputProps: InputPropsFromParent,
  ...props
}: SearchBoxProps & TextFieldProps) {
  const handleKeyPress = (event: KeyboardEvent) => {
    if (!submitOnEnter) {
      return;
    }
    if (event.key === 'Enter') {
      if (onSearch) {
        onSearch(value);
      }
    }
  };

  return (
    <SearchBoxContext.Provider value={{ variant, chrome, value, onSearch, onClear }}>
      <StyledTextField
        {...props}
        $chrome={chrome}
        variant={variant}
        fullWidth
        value={value}
        InputProps={{
          ...InputPropsFromParent,
          startAdornment: <StartAdornment />,
          endAdornment: <EndAdornment />
        }}
        inputProps={inputPropsFromParent}
        onKeyPress={variant === 'outlined' ? handleKeyPress : undefined}
      />
    </SearchBoxContext.Provider>
  );
});
